import React, { useState, useEffect } from 'react';
import axios from 'axios';
import EditableListField from './EditableListField';
import './ResumeDirectionGradesSubTab.css';

const API_URL = process.env.REACT_APP_API_URL || '';
const GRADES = ['Junior', 'Middle', 'Senior'];

function ensureArray(arr) {
  return Array.isArray(arr) ? arr : [];
}

function ResumeDirectionGradesSubTab() {
  const [directions, setDirections] = useState([]);
  const [grades, setGrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingGrade, setEditingGrade] = useState(null);
  const [formData, setFormData] = useState({
    direction_id: '',
    grade: 'Junior',
    technologies: [],
    skills: []
  });

  const fetchDirections = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/resume-directions`);
      setDirections(response.data);
    } catch (err) {
      setError('Ошибка загрузки направлений: ' + (err.response?.data?.detail || err.message));
    }
  };

  const fetchGrades = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/resume-direction-grades`);
      setGrades(response.data);
      setError(null);
    } catch (err) {
      setError('Ошибка при загрузке: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDirections();
  }, []);

  useEffect(() => {
    fetchGrades();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const directionId = parseInt(formData.direction_id, 10);
    if (!directionId) {
      setError('Выберите направление');
      return;
    }
    const technologies = ensureArray(formData.technologies);
    const skills = ensureArray(formData.skills);
    try {
      if (editingGrade) {
        await axios.put(`${API_URL}/api/resume-direction-grades/${editingGrade.id}`, {
          direction_id: directionId,
          grade: formData.grade,
          technologies,
          skills
        });
      } else {
        await axios.post(`${API_URL}/api/resume-direction-grades`, {
          direction_id: directionId,
          grade: formData.grade,
          technologies,
          skills
        });
      }
      setShowForm(false);
      setEditingGrade(null);
      setFormData({ direction_id: '', grade: 'Junior', technologies: [], skills: [] });
      fetchGrades();
      setError(null);
    } catch (err) {
      setError('Ошибка при сохранении: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleEdit = (row) => {
    setEditingGrade(row);
    setFormData({
      direction_id: String(row.direction_id),
      grade: row.grade,
      technologies: ensureArray(row.technologies),
      skills: ensureArray(row.skills)
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Удалить эту запись?')) return;
    try {
      await axios.delete(`${API_URL}/api/resume-direction-grades/${id}`);
      fetchGrades();
    } catch (err) {
      setError('Ошибка при удалении: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingGrade(null);
    setFormData({ direction_id: '', grade: 'Junior', technologies: [], skills: [] });
  };

  if (loading) {
    return <div className="loading">Загрузка...</div>;
  }

  return (
    <div className="resume-direction-grades-sub-tab">
      <div className="tab-header">
        <h2>Технологии грейдов направлений</h2>
        {!showForm && (
          <button
            className="btn btn-primary"
            onClick={() => {
              setFormData({ direction_id: directions[0]?.id ? String(directions[0].id) : '', grade: 'Junior', technologies: [], skills: [] });
              setShowForm(true);
            }}
            disabled={directions.length === 0}
            title={directions.length === 0 ? 'Сначала добавьте направление во вкладке «Направления резюме»' : ''}
          >
            + Добавить запись
          </button>
        )}
      </div>

      {error && <div className="error-message">{error}</div>}
      {directions.length === 0 && !showForm && (
        <p className="hint">Сначала добавьте направление во вкладке «Направления резюме».</p>
      )}

      {showForm && (
        <div className="form-container">
          <h3>{editingGrade ? 'Редактировать запись' : 'Новая запись'}</h3>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="grade_direction">Направление *</label>
              <select
                id="grade_direction"
                value={formData.direction_id}
                onChange={(e) => setFormData({ ...formData, direction_id: e.target.value })}
                required
                disabled={!!editingGrade}
              >
                <option value="">— Выберите —</option>
                {directions.map((d) => (
                  <option key={d.id} value={d.id}>{d.name}</option>
                ))}
              </select>
              {editingGrade && <small className="form-hint">Направление нельзя изменить при редактировании.</small>}
            </div>
            <div className="form-group">
              <label htmlFor="grade_grade">Грейд *</label>
              <select
                id="grade_grade"
                value={formData.grade}
                onChange={(e) => setFormData({ ...formData, grade: e.target.value })}
                required
              >
                {GRADES.map((g) => (
                  <option key={g} value={g}>{g}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <EditableListField
                label="Технологии"
                value={formData.technologies}
                onChange={(technologies) => setFormData({ ...formData, technologies })}
                placeholder="Например: React, TypeScript"
                addButtonLabel="+ Добавить технологию"
              />
            </div>
            <div className="form-group">
              <EditableListField
                label="Навыки соискателя"
                value={formData.skills}
                onChange={(skills) => setFormData({ ...formData, skills })}
                placeholder="Например: Умение писать тесты"
                addButtonLabel="+ Добавить навык"
              />
            </div>
            <div className="form-actions">
              <button type="submit" className="btn btn-primary">
                {editingGrade ? 'Сохранить' : 'Создать'}
              </button>
              <button type="button" className="btn btn-secondary" onClick={handleCancel}>
                Отмена
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="table-container">
        <table className="data-table">
          <thead>
              <tr>
              <th>ID</th>
              <th>Направление</th>
              <th>Грейд</th>
              <th>Технологии</th>
              <th>Навыки</th>
              <th>Действия</th>
            </tr>
          </thead>
          <tbody>
            {grades.length === 0 ? (
              <tr>
                <td colSpan="6" className="empty-state">
                  Нет записей. Добавьте первую.
                </td>
              </tr>
            ) : (
              grades.map((g) => (
                <tr key={g.id}>
                  <td>{g.id}</td>
                  <td>{g.direction_name || '—'}</td>
                  <td>{g.grade}</td>
                  <td className="cell-list">
                    {Array.isArray(g.technologies) && g.technologies.length > 0
                      ? g.technologies.map((t, i) => <span key={i} className="list-tag">{t}</span>)
                      : '—'}
                  </td>
                  <td className="cell-list">
                    {Array.isArray(g.skills) && g.skills.length > 0
                      ? g.skills.map((s, i) => <span key={i} className="list-tag">{s}</span>)
                      : '—'}
                  </td>
                  <td>
                    <button className="btn-icon btn-edit" onClick={() => handleEdit(g)} title="Редактировать">✏️</button>
                    <button className="btn-icon btn-delete" onClick={() => handleDelete(g.id)} title="Удалить">✕</button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default ResumeDirectionGradesSubTab;
